import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { CheckCircle2, ShieldCheck } from "lucide-react"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
} from "@/components"
import { api } from "../system/api.js"
import { getSymbolCurrency } from "../system/util.js"
import { getStripePromise } from "../system/stripePromise.js"
import { buildStripeAppearance } from "../system/stripeAppearance.js"
import Loader from "../components-legacy/Loader.jsx"
import SmallLogo from "../assets/svg/logo-algobuilder.svg?react"

export default function PaymentCheckout() {
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()

	const productId = searchParams.get("id")
	const rawType = searchParams.get("type")

	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [product, setProduct] = useState(null)
	const [paymentMethods, setPaymentMethods] = useState([])
	const [stripePromise, setStripePromise] = useState(null)
	const [clientSecret, setClientSecret] = useState(null)

	const isUnsupportedType = rawType && rawType !== "invoice"

	useEffect(() => {
		if (isUnsupportedType) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setLoading(false)
			return
		}
		if (!productId) {
			setError("No invoice selected")
			setLoading(false)
			return
		}

		let cancelled = false
		setLoading(true)

		api("payment/initiate", { type: "invoice", id: parseInt(productId) })
			.then((res) => {
				if (cancelled) return
				setProduct(res.product)
				setPaymentMethods(res.paymentMethods || [])
				if (res.stripePublicKey) {
					setStripePromise(getStripePromise(res.stripePublicKey))
				}
				if (res.clientSecret) {
					setClientSecret(res.clientSecret)
				}
			})
			.catch((err) => {
				if (cancelled) return
				console.error("Failed to initiate payment:", err)
				setError(
					err.errorCode === "INVOICE_ALREADY_PAID"
						? "This invoice has already been paid"
						: err.errorCode === "INVOICE_STATUS_INVALID"
							? "This invoice cannot be paid at this time"
							: "Failed to initiate payment"
				)
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [productId, isUnsupportedType])

	if (loading) {
		return <Loader />
	}

	return (
		<div className="v1 min-h-full bg-background text-foreground">
			<header className="border-b border-border">
				<div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
					<SmallLogo className="w-8 h-auto" />
					<span className="text-body font-medium">AlgoBuilder</span>
				</div>
			</header>

			<main className="max-w-5xl mx-auto px-4 py-8">
				{isUnsupportedType ? (
					<CheckoutErrorCard
						title="Checkout link is no longer supported"
						description="This payment link is no longer supported. Plan changes are now handled from your billing settings."
						onBack={() => navigate("/billing")}
					/>
				) : error ? (
					<CheckoutErrorCard
						title="Unable to load invoice"
						description={error}
						onBack={() => navigate("/billing")}
					/>
				) : !product ? (
					<CheckoutErrorCard
						title="Invoice not found"
						description="We couldn't find this invoice. It may have been removed or already paid."
						onBack={() => navigate("/billing")}
					/>
				) : (
					<>
						<h1 className="text-section text-foreground mb-6">Pay invoice</h1>
						{stripePromise && clientSecret ? (
							<Elements
								stripe={stripePromise}
								options={{
									clientSecret,
									appearance: buildStripeAppearance(),
									loader: "auto",
								}}
							>
								<InvoicePaymentForm
									product={product}
									paymentMethods={paymentMethods}
									productId={productId}
								/>
							</Elements>
						) : (
							<div className="h-32 rounded-md border border-border bg-card shimmer-bar" />
						)}
					</>
				)}
			</main>
		</div>
	)
}

function CheckoutErrorCard({ title, description, onBack }) {
	return (
		<div className="max-w-xl mx-auto rounded-md border border-border bg-card p-6 flex flex-col gap-4">
			<div>
				<h2 className="text-section text-foreground font-semibold">{title}</h2>
				<p className="text-body text-muted-foreground mt-1">{description}</p>
			</div>
			<Button variant="outline" size="sm" className="text-label w-fit" onClick={onBack}>
				Back to billing
			</Button>
		</div>
	)
}

function InvoicePaymentForm({ product, paymentMethods, productId }) {
	const stripe = useStripe()
	const elements = useElements()
	const navigate = useNavigate()

	const [processing, setProcessing] = useState(false)
	const [paymentProcessed, setPaymentProcessed] = useState(false)
	const [error, setError] = useState(null)
	const [selectedMethod, setSelectedMethod] = useState(
		paymentMethods.length > 0 ? paymentMethods[0].id : "new"
	)

	const currencySymbol = getSymbolCurrency(product.currency)
	const order = buildInvoiceOrder({ product, currencySymbol })

	const paymentMethodOptions = [
		...paymentMethods.map((m) => ({
			value: m.id,
			label: `${m.brand.charAt(0).toUpperCase() + m.brand.slice(1)} ending in ${m.last4} (Exp: ${String(m.exp_month).padStart(2, "0")}/${m.exp_year})`,
		})),
		{ value: "new", label: "Use a new card" },
	]

	const handleSubmit = async (e) => {
		e.preventDefault()
		if (!stripe || !elements) return

		setProcessing(true)
		setError(null)

		try {
			let paymentData = {}

			if (selectedMethod === "new") {
				const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
					elements,
					confirmParams: {
						return_url: window.location.origin + "/checkout/complete",
					},
					redirect: "if_required",
				})

				if (confirmError) {
					if (
						confirmError.type === "card_error" ||
						confirmError.type === "validation_error"
					) {
						setError(confirmError.message)
					} else {
						setError("The payment could not be processed. Please try again.")
					}
					setProcessing(false)
					return
				}

				if (paymentIntent?.status !== "succeeded") {
					setError("The payment was not successful. Please try again.")
					setProcessing(false)
					return
				}

				paymentData = {
					paymentIntentId: paymentIntent.id,
					paymentMethodId: paymentIntent.payment_method,
				}
			} else {
				paymentData = { paymentMethodId: selectedMethod }
			}

			await api("payment/confirm", {
				type: "invoice",
				id: parseInt(productId),
				paymentData,
			})

			setPaymentProcessed(true)
			setTimeout(() => {
				navigate("/billing")
			}, 3000)
		} catch (err) {
			console.error("Payment error:", err)
			setError(err.message || "Payment failed. Please try again.")
			setProcessing(false)
		}
	}

	if (paymentProcessed) {
		return (
			<div className="max-w-xl mx-auto rounded-md border border-border bg-card p-6 flex flex-col items-start gap-3">
				<CheckCircle2 className="text-success" size={32} strokeWidth={1.75} />
				<div>
					<h2 className="text-section text-foreground font-semibold">Payment successful</h2>
					<p className="text-body text-muted-foreground mt-1">
						Your invoice has been paid. Redirecting you to billing in a few seconds.
					</p>
				</div>
			</div>
		)
	}

	return (
		<form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
			<div>
				<h2 className="text-section text-foreground mb-4">Order Summary</h2>
				<div className="bg-card border border-border rounded-md p-5">
					<h3 className="text-section text-foreground font-semibold mb-3">
						{order.summaryTitle}
					</h3>
					<div className="flex flex-col gap-2 text-body">
						{order.summaryRows.map((rowItem, i) => (
							<div key={i} className="flex justify-between">
								<span className="text-muted-foreground">{rowItem.label}</span>
								<span className={rowItem.valueClassName ?? "text-foreground"}>
									{rowItem.value}
								</span>
							</div>
						))}
					</div>
					<Separator className="my-4" />
					<div className="flex justify-between text-body font-semibold">
						<span className="text-foreground">Total:</span>
						<span className="text-primary text-section">{order.totalPrice}</span>
					</div>
					{order.footer && (
						<p className="text-caption text-muted-foreground mt-3">{order.footer}</p>
					)}
				</div>
			</div>

			<div>
				<h2 className="text-section text-foreground mb-4">Payment</h2>
				<div className="bg-card border border-border rounded-md p-5">
					<p className="text-caption text-muted-foreground mb-4">
						Secure checkout. Your card is never stored.
					</p>

					{paymentMethods.length > 0 && (
						<div className="mb-4">
							<label className="text-caption text-muted-foreground mb-1 block">
								Payment method
							</label>
							<Select value={selectedMethod} onValueChange={setSelectedMethod}>
								<SelectTrigger className="w-full bg-muted border-border text-body">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{paymentMethodOptions.map((opt) => (
										<SelectItem key={opt.value} value={opt.value} className="text-body">
											{opt.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{selectedMethod === "new" && (
						<div className="mb-4">
							<PaymentElement options={{ layout: "tabs" }} />
						</div>
					)}

					{error && <p className="text-caption text-danger mb-3">{error}</p>}

					<Button
						type="submit"
						className="w-full text-label"
						disabled={!stripe || processing}
					>
						{processing ? "Processing..." : order.ctaLabel}
					</Button>
				</div>
				<p className="text-caption text-muted-foreground mt-3 font-medium flex items-center gap-1.5">
					<ShieldCheck size={13} strokeWidth={1.5} className="shrink-0" />
					Your payment information is secure and encrypted.
				</p>
			</div>
		</form>
	)
}

function buildInvoiceOrder({ product, currencySymbol }) {
	const status = String(product.status || "").toLowerCase()
	const isOverdue = status === "overdue" || status === "past_due"
	const statusLabel = product.status
		? product.status.charAt(0).toUpperCase() + product.status.slice(1)
		: "Unpaid"

	const summaryRows = [
		{ label: "Invoice #:", value: `#${product.invoiceId}` },
	]

	if (product.dueDate) {
		summaryRows.push({
			label: "Due date:",
			value: new Date(product.dueDate).toLocaleDateString(),
		})
	}

	summaryRows.push({
		label: "Status:",
		value: statusLabel,
		valueClassName: isOverdue ? "text-danger font-medium" : "text-foreground",
	})

	const totalPrice = `${currencySymbol}${product.price}`
	const ctaLabel = `Pay ${totalPrice}`

	const footer = product.isSubscription
		? "Your subscription will be renewed upon successful payment."
		: null

	return {
		summaryTitle: product.name,
		summaryRows,
		totalPrice,
		ctaLabel,
		footer,
	}
}
